import React, { useState, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';
import SEO from '../components/SEO';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Gallery = () => {
  const [selectedCategory, setSelectedCategory] = useState('varanasi');
  const [images, setImages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const categories = [
    { id: 'varanasi', label: 'Varanasi & Surroundings' },
    { id: 'deluxe', label: 'Deluxe Rooms' },
    { id: 'premium', label: 'Standard Room' },
    { id: 'ultra-large', label: 'Ultra-Large Room' }
  ];

  useEffect(() => {
    fetchImages();
  }, [selectedCategory]);

  const fetchImages = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/gallery?category=${selectedCategory}`);
      setImages(response.data);
    } catch (error) {
      console.error('Error fetching images:', error);
      setImages([]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen">
      <SEO
        title="Photo Gallery — Ved Homestays Varanasi Rooms, Ghats & Temple Views"
        description="Browse photos of Ved Homestays rooms, Varanasi ghats, Ganga Aarti, and Kashi Vishwanath Temple area. See our Deluxe, Standard and Ultra-Large rooms near the sacred ghats of Varanasi."
        keywords="varanasi hotel photos, ved homestays gallery, kashi vishwanath temple photos, ganga ghat images, varanasi room photos, homestay varanasi images, hotel near ghat photos, varanasi accommodation gallery"
        url="https://www.vedhomestays.com/gallery"
        breadcrumbs={[
          { name: "Home", url: "https://www.vedhomestays.com/" },
          { name: "Gallery", url: "https://www.vedhomestays.com/gallery" }
        ]}
      />

      {/* Hero Section */}
      <section className="relative h-80 flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1734784566171-1c4cc78df1c1"
            alt="Varanasi Boats"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 to-black/50"></div>
        </div>
        <div className="relative z-10 text-center text-white px-4">
          <h1 className="text-5xl md:text-6xl font-bold mb-4">Gallery — Ved Homestays & Varanasi</h1>
          <p className="text-xl md:text-2xl text-orange-300">Rooms, Ghats & Temple Views in the Holy City</p>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="py-20 bg-gradient-to-b from-white to-orange-50">
        <div className="container mx-auto px-4">
          {/* Category Selector */}
          <div className="max-w-2xl mx-auto mb-12">
            <label className="block text-lg font-semibold text-gray-800 mb-3 text-center">
              Select Category
            </label>
            <div className="relative">
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="w-full px-6 py-4 bg-white border-2 border-orange-200 rounded-xl text-gray-800 font-medium text-lg appearance-none cursor-pointer hover:border-orange-400 focus:outline-none focus:border-orange-500 transition-colors"
              >
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.label}
                  </option>
                ))}
              </select>
              <ChevronDown
                className="absolute right-6 top-1/2 transform -translate-y-1/2 text-orange-600 pointer-events-none"
                size={24}
              />
            </div>
          </div>

          {/* Images Grid */}
          <div className="max-w-7xl mx-auto">
            {isLoading ? (
              <div className="text-center py-20">
                <div className="inline-block w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
                <p className="text-gray-500 mt-4">Loading images...</p>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {images.map((image, index) => (
                    <div
                      key={index}
                      className="group relative overflow-hidden rounded-xl shadow-md hover:shadow-2xl transition-all duration-300 aspect-[4/3] cursor-pointer"
                    >
                      <img
                        src={image.url}
                        alt={image.caption}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                      {image.caption && (
                        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                          <div className="absolute bottom-0 left-0 right-0 p-4">
                            <p className="text-white font-medium text-sm">{image.caption}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {images.length === 0 && (
                  <div className="text-center py-20">
                    <p className="text-gray-500 text-lg">No images available for this category yet.</p>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Gallery;
